import { Box, Button, Checkmark, TransferStatusEnum, VStack, type Transfer } from '@talos/kyoko';
import { useCallback, type MouseEvent } from 'react';

import {
  TransferConfirmationActions,
  TransferConfirmationContent,
  TransferConfirmationOverlay as TransferConfirmationOverlayShell,
  TransferConfirmationPopperHeader,
  TransferConfirmationWrapper,
} from './styles';
import { TransferCompletedContent } from './TransferCompletedContent';
import { TransferProcessingContent } from './TransferProcessingContent';
import { useTrackedTransfer } from './useTrackedTransfer';

export interface PendingTransferConfirmation {
  clientID: string;
  transferID?: string;
  seedTransfer?: Transfer;
}

interface TransferConfirmationOverlayProps {
  pendingConfirmation: PendingTransferConfirmation;
  isVisible: boolean;
  onClose: () => void;
  onResubmit: () => void;
}

export function TransferConfirmationOverlay({
  pendingConfirmation,
  isVisible,
  onClose,
  onResubmit,
}: TransferConfirmationOverlayProps) {
  const trackedTransfer = useTrackedTransfer(pendingConfirmation.clientID, pendingConfirmation.transferID);
  const transfer = trackedTransfer ?? pendingConfirmation.seedTransfer;
  const isRejected = transfer?.Status === TransferStatusEnum.Rejected;
  const showResultContent = transfer?.isTerminal === true;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOverlayMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <TransferConfirmationOverlayShell isVisible={isVisible} onMouseDown={handleOverlayMouseDown}>
      {!isRejected && (
        <VStack>
          <Checkmark />
          <Box mt="spacing16" fontWeight="bold" color="colorTextImportant" data-testid="transfer-confirmation-status">
            Transfer Initiated
          </Box>
        </VStack>
      )}
      <TransferConfirmationWrapper isVisible={isVisible} data-testid="transfer-form-confirmation">
        <TransferConfirmationPopperHeader title="Transfer Confirmation" onCloseClicked={handleClose} />
        <TransferConfirmationContent>
          {showResultContent && transfer != null ? (
            <TransferCompletedContent transfer={transfer} onResubmit={onResubmit} />
          ) : (
            <TransferProcessingContent status={transfer?.Status} onResubmit={onResubmit} />
          )}
        </TransferConfirmationContent>
        <TransferConfirmationActions>
          <Button dim onClick={handleClose} data-testid="transfer-confirmation-close">
            Close Confirmation
          </Button>
        </TransferConfirmationActions>
      </TransferConfirmationWrapper>
    </TransferConfirmationOverlayShell>
  );
}
